import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, money } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Plus, Trash2, TrendingUp, TrendingDown, DollarSign } from "lucide-react";
import { toast } from "sonner";

/**
 * FinanceTab — unified money-in + money-out ledger.
 *
 * Backed by the `budget_items` collection with a `kind` field ("expense" |
 * "revenue"). The tab is deliberately still exported under the legacy name
 * `BudgetTab` so imports elsewhere keep working during the rename rollout.
 */

const EXPENSE_CATEGORIES = ["Venue", "Catering", "Photography", "Video", "Florals", "Music", "Attire", "Rentals", "Stationery", "Transportation", "Beauty", "Other"];
const REVENUE_CATEGORIES = ["Ticket Sales", "Sponsorship", "Vendor Fees", "Registration", "Client Payment", "Deposit", "Donation", "Merchandise", "Other"];

const EXPENSE_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "deposit-paid", label: "Deposit paid" },
  { value: "paid", label: "Paid in full" },
  { value: "overdue", label: "Overdue" },
];
const REVENUE_STATUSES = [
  { value: "pending", label: "Awaiting payment" },
  { value: "deposit-paid", label: "Deposit received" },
  { value: "received", label: "Paid in full" },
  { value: "overdue", label: "Overdue" },
];

const emptyForm = (kind) => ({
  kind,
  category: kind === "revenue" ? "Ticket Sales" : "Venue",
  item: "",
  planned: 0,
  actual: 0,
  vendor_member_id: "",
  status: "pending",
  notes: "",
});

export default function BudgetTab({ eventId, members, canEdit }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [openKind, setOpenKind] = useState(null); // null | "expense" | "revenue"
  const [form, setForm] = useState(emptyForm("expense"));
  const vendors = useMemo(() => members.filter((m) => m.role === "vendor"), [members]);

  const load = useCallback(async () => {
    try {
      const [{ data: rows }, { data: sum }] = await Promise.all([
        api.get(`/events/${eventId}/budget`),
        api.get(`/events/${eventId}/finance/summary`),
      ]);
      setItems(rows);
      setSummary(sum);
    } catch { toast.error("Could not load finance data"); }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const startAdd = (kind) => { setForm(emptyForm(kind)); setOpenKind(kind); };

  const submitAdd = async () => {
    if (!form.item) { toast.error("Add an item name"); return; }
    try {
      await api.post(`/events/${eventId}/budget`, {
        ...form,
        planned: Number(form.planned) || 0,
        actual: Number(form.actual) || 0,
        vendor_member_id: form.vendor_member_id || null,
      });
      setOpenKind(null);
      load();
      toast.success(form.kind === "revenue" ? "Revenue line added" : "Expense line added");
    } catch { toast.error("Could not add"); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this line?")) return;
    try { await api.delete(`/events/${eventId}/budget/${id}`); load(); }
    catch { toast.error("Could not delete"); }
  };

  const update = async (id, field, value) => {
    try {
      const payload = { [field]: (field === "planned" || field === "actual") ? Number(value) : value };
      await api.patch(`/events/${eventId}/budget/${id}`, payload);
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...payload } : i)));
      // Re-fetch summary so P&L stays live.
      api.get(`/events/${eventId}/finance/summary`).then(({ data }) => setSummary(data)).catch(() => {});
    } catch { toast.error("Could not save"); }
  };

  const revenue = useMemo(() => items.filter((i) => i.kind === "revenue"), [items]);
  const expenses = useMemo(() => items.filter((i) => i.kind !== "revenue"), [items]);

  return (
    <div className="space-y-8" data-testid="finance-tab">
      <PLSummary summary={summary} />

      <LedgerSection
        title="Money In"
        subtitle="Revenue · ticket sales · sponsorships · client payments"
        icon={<TrendingUp size={14} strokeWidth={2} />}
        accent="emerald"
        kind="revenue"
        items={revenue}
        vendors={vendors}
        canEdit={canEdit}
        onAdd={() => startAdd("revenue")}
        onUpdate={update}
        onDelete={del}
      />

      <LedgerSection
        title="Money Out"
        subtitle="Expenses · vendor payments · outstanding balances"
        icon={<TrendingDown size={14} strokeWidth={2} />}
        accent="rose"
        kind="expense"
        items={expenses}
        vendors={vendors}
        canEdit={canEdit}
        onAdd={() => startAdd("expense")}
        onUpdate={update}
        onDelete={del}
      />

      <AddLineDialog
        open={!!openKind}
        onClose={() => setOpenKind(null)}
        form={form}
        setForm={setForm}
        onSubmit={submitAdd}
        vendors={vendors}
      />
    </div>
  );
}

/* ---------- P&L summary block ---------- */
function PLSummary({ summary }) {
  const rev = summary?.revenue || { planned: 0, actual: 0, outstanding: 0 };
  const exp = summary?.expenses || { planned: 0, actual: 0, outstanding: 0 };
  const profit = summary?.profit || { actual: 0, planned: 0, margin_pct: null };
  const marginLabel = profit.margin_pct == null ? "—" : `${profit.margin_pct.toFixed(1)}%`;
  const profitPositive = profit.actual >= 0;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="finance-pl-summary">
      <SummaryCard
        label="Revenue"
        sub={`Planned ${money(rev.planned)}`}
        value={money(rev.actual)}
        accent="emerald"
        icon={<TrendingUp size={14} strokeWidth={2} />}
      />
      <SummaryCard
        label="Expenses"
        sub={`Planned ${money(exp.planned)}`}
        value={money(exp.actual)}
        accent="rose"
        icon={<TrendingDown size={14} strokeWidth={2} />}
      />
      <SummaryCard
        label={profitPositive ? "Profit" : "Loss"}
        sub={`Projected ${money(profit.planned)}`}
        value={money(Math.abs(profit.actual))}
        accent={profitPositive ? "emerald" : "rose"}
        icon={<DollarSign size={14} strokeWidth={2} />}
      />
      <SummaryCard
        label="Net Margin"
        sub={`Outstanding ${money(rev.outstanding + exp.outstanding)}`}
        value={marginLabel}
        accent={profit.margin_pct != null && profit.margin_pct >= 0 ? "emerald" : "rose"}
        icon={<DollarSign size={14} strokeWidth={2} />}
      />
    </div>
  );
}

function SummaryCard({ label, sub, value, accent, icon }) {
  const accentCls = accent === "emerald"
    ? "text-emerald-600 dark:text-emerald-400"
    : "text-[#DC2626] dark:text-red-400";
  return (
    <div className="bevel border-hair rounded-sm p-4" data-testid={`finance-summary-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`eyebrow mb-2 flex items-center gap-1.5 ${accentCls}`}>{icon} {label}</div>
      <div className={`font-serif text-2xl md:text-3xl ${accentCls} tabular-nums`}>{value}</div>
      <div className="text-[11px] text-muted mt-1">{sub}</div>
    </div>
  );
}

/* ---------- Ledger section (one per kind) ---------- */
function LedgerSection({ title, subtitle, icon, accent, kind, items, vendors, canEdit, onAdd, onUpdate, onDelete }) {
  const totals = items.reduce(
    (s, i) => ({ planned: s.planned + (i.planned || 0), actual: s.actual + (i.actual || 0) }),
    { planned: 0, actual: 0 },
  );
  const STATUSES = kind === "revenue" ? REVENUE_STATUSES : EXPENSE_STATUSES;

  return (
    <section data-testid={`finance-ledger-${kind}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className={`font-mono text-[10px] tracking-[0.2em] uppercase mb-2 flex items-center gap-1.5 ${accent === "emerald" ? "text-emerald-600 dark:text-emerald-400" : "text-[#DC2626] dark:text-red-400"}`}>
            {icon} {title}
          </div>
          <div className="text-sm text-muted">{subtitle}</div>
        </div>
        {canEdit && (
          <Button
            onClick={onAdd}
            data-testid={`add-${kind}-btn`}
            className="rounded-sm bg-app hover:bg-[#000000]"
          >
            <Plus size={14} strokeWidth={1.75} className="mr-1.5" />
            Add {kind === "revenue" ? "income" : "expense"}
          </Button>
        )}
      </div>

      <div className="bevel border-hair rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-hair">
                {["Category", "Item", "Party", "Planned", "Actual", "Status", ""].map((h) => (
                  <th key={h} className="text-left eyebrow text-muted px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-muted font-light text-sm">
                    {kind === "revenue"
                      ? "No revenue yet. Add ticket sales, sponsorship deals, or client payments."
                      : "No expenses yet. Add a line for venue, catering, or any planned cost."}
                  </td>
                </tr>
              )}
              {items.map((i) => {
                const vendor = vendors.find((v) => v.id === i.vendor_member_id);
                return (
                  <tr key={i.id} className="border-b border-hair/60 hover:bg-canvas/40" data-testid={`finance-row-${i.id}`}>
                    <td className="px-4 py-3 text-sm text-muted">{i.category}</td>
                    <td className="px-4 py-3 text-app">{i.item}</td>
                    <td className="px-4 py-3 text-sm text-muted">{vendor ? vendor.name : "—"}</td>
                    <td className="px-4 py-3 text-app">
                      {canEdit ? (
                        <Input type="number" defaultValue={i.planned} onBlur={(e) => onUpdate(i.id, "planned", e.target.value)}
                          className="w-24 h-8 rounded-sm text-sm" />
                      ) : money(i.planned)}
                    </td>
                    <td className="px-4 py-3 text-app">
                      {canEdit ? (
                        <Input type="number" defaultValue={i.actual} onBlur={(e) => onUpdate(i.id, "actual", e.target.value)}
                          className="w-24 h-8 rounded-sm text-sm" />
                      ) : money(i.actual)}
                    </td>
                    <td className="px-4 py-3">
                      {canEdit ? (
                        <Select value={i.status} onValueChange={(v) => onUpdate(i.id, "status", v)}>
                          <SelectTrigger className="w-40 h-8 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : STATUSES.find((s) => s.value === i.status)?.label}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canEdit && (
                        <button data-testid={`delete-finance-${i.id}`} onClick={() => onDelete(i.id)} className="text-muted hover:text-[#EF4444]">
                          <Trash2 size={14} strokeWidth={1.5} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {items.length > 0 && (
              <tfoot>
                <tr className="bg-canvas">
                  <td colSpan={3} className="px-4 py-3 eyebrow">Totals</td>
                  <td className="px-4 py-3 font-medium text-app tabular-nums">{money(totals.planned)}</td>
                  <td className="px-4 py-3 font-medium text-app tabular-nums">{money(totals.actual)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </section>
  );
}

/* ---------- Add-line dialog (dual-mode) ---------- */
function AddLineDialog({ open, onClose, form, setForm, onSubmit, vendors }) {
  const isRevenue = form.kind === "revenue";
  const CATS = isRevenue ? REVENUE_CATEGORIES : EXPENSE_CATEGORIES;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-surface rounded-sm max-w-lg" data-testid="finance-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-app flex items-center gap-2">
            {isRevenue
              ? <><TrendingUp size={18} className="text-emerald-500" /> New revenue line</>
              : <><TrendingDown size={18} className="text-[#DC2626]" /> New expense line</>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <F label="Category">
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="finance-category-select" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label={isRevenue ? "Source" : "Item"}>
            <Input
              data-testid="finance-item-input"
              value={form.item}
              onChange={(e) => setForm({ ...form, item: e.target.value })}
              placeholder={isRevenue ? "e.g. Silver-tier sponsorship" : "e.g. Ceremony arch florals"}
            />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label={isRevenue ? "Expected ($)" : "Planned ($)"}>
              <Input
                data-testid="finance-planned-input"
                type="number"
                value={form.planned}
                onChange={(e) => setForm({ ...form, planned: e.target.value })}
              />
            </F>
            <F label={isRevenue ? "Received ($)" : "Actual ($)"}>
              <Input
                type="number"
                value={form.actual}
                onChange={(e) => setForm({ ...form, actual: e.target.value })}
              />
            </F>
          </div>
          {!isRevenue && (
            <F label="Vendor (optional)">
              <Select
                value={form.vendor_member_id || "none"}
                onValueChange={(v) => setForm({ ...form, vendor_member_id: v === "none" ? "" : v })}
              >
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} · {v.vendor_category}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="finance-submit" onClick={onSubmit} className="rounded-sm bg-app hover:bg-[#000000]">Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return (<div><Label className="eyebrow block mb-2">{label}</Label>{children}</div>);
}
